/**
 * Indicative market prices (web) — Phase 3, Slice 6.
 *
 * A read-only view of recent indicative price bands per (product, grade), e.g.
 * "Today's mango price: R12–R15/kg". Backed by the PUBLIC GET /market/prices
 * endpoint (no auth required) via the orval-generated client (TD-103), though
 * the page itself sits behind the _app auth gate and is shown to farmers and
 * coop-admins (the nav item is role-gated in _app.tsx).
 *
 * The list endpoint uses a { data: [...] } envelope, so we read `.data` off the
 * generated `IndicativePriceListDto`. The micro-ZARP→ZARP money helper still
 * lives in src/api/market.ts (a pure formatter orval can't model).
 *
 * Money is carried on the wire as micro-ZARP (1e-6 ZARP) STRINGS; we convert to
 * whole ZARP with microToZarp() and render with the @fse/ui <Money> + <StatCard>
 * kit. i18n keys live under market.* in all four web locales.
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Money,
  Stack,
  StatCard,
  Text,
} from '@fse/ui';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { useMarketPublicControllerGetPrices } from '../api/generated/market/market';
import type { IndicativePriceDto } from '../api/generated/models';
import { microToZarp } from '../api/market';

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
});

function formatObservedAt(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return dateFormatter.format(parsed);
}

export function MarketScreen() {
  const { t } = useTranslation();

  // PUBLIC endpoint — no params narrows to the default (product=mango, any grade).
  // List endpoints use a { data: [...] } envelope; read `.data` off it.
  const pricesQuery = useMarketPublicControllerGetPrices();

  const prices = pricesQuery.data?.data ?? [];
  // TD-121: the response now carries `asOf` (freshest observedAt). The generated
  // IndicativePriceListDto type predates it (orval regen pending, TD-103), so cast.
  const asOf = (pricesQuery.data as { asOf?: string | null } | undefined)?.asOf ?? null;

  return (
    <Stack spacing={3}>
      <Box>
        <Text variant="headline-medium" as="h2">
          {t('market.title')}
        </Text>
        <Box sx={{ mt: 0.5 }}>
          <Text variant="body-medium" color="text.secondary">
            {t('market.subtitle')}
          </Text>
        </Box>
      </Box>

      {asOf ? (
        <Text variant="label-medium" color="text.secondary">
          {t('market.asOf', { date: formatObservedAt(asOf) })}
        </Text>
      ) : null}

      {pricesQuery.isPending ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : pricesQuery.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => pricesQuery.refetch()}>
              {t('market.retry')}
            </Button>
          }
        >
          {t('market.loadError', {
            error:
              pricesQuery.error instanceof Error
                ? pricesQuery.error.message
                : String(pricesQuery.error),
          })}
        </Alert>
      ) : prices.length === 0 ? (
        <Card>
          <CardContent>
            <Text variant="body-medium" color="text.secondary">
              {t('market.empty')}
            </Text>
          </CardContent>
        </Card>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 2,
          }}
        >
          {prices.map((price) => (
            <PriceCard
              key={`${price.product}-${price.grade}`}
              price={price}
              t={t}
            />
          ))}
        </Box>
      )}

      <Text variant="body-small" color="text.secondary">
        {t('market.disclaimer')}
      </Text>
    </Stack>
  );
}

interface PriceCardProps {
  price: IndicativePriceDto;
  t: TFunction;
}

function PriceCard({ price, t }: PriceCardProps) {
  const low = microToZarp(price.lowMicro);
  const high = microToZarp(price.highMicro);
  const latest = microToZarp(price.latestMicro);

  const gradeLabel = price.grade
    ? t('market.grade', { grade: price.grade })
    : t('market.gradeAny');

  return (
    <Card>
      <CardContent>
        <Stack spacing={1.5}>
          <Box>
            <Text variant="title-large" as="span">
              {price.product}
            </Text>
            <Box sx={{ mt: 0.25 }}>
              <Text variant="label-medium" color="text.secondary">
                {gradeLabel}
              </Text>
            </Box>
          </Box>

          {/* Band low–high */}
          <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
            <Money amount={low} unit="" size="lg" />
            <Text variant="title-medium" color="text.secondary" as="span">
              –
            </Text>
            <Money amount={high} unit={t('market.perKg')} size="lg" />
          </Stack>

          <StatCard
            color="leaf"
            label={t('market.latest')}
            value={<Money amount={latest} unit={t('market.perKg')} size="md" color="inherit" />}
          />

          <Text variant="label-small" color="text.secondary">
            {t('market.observedAt', { date: formatObservedAt(price.observedAt) })}
          </Text>
        </Stack>
      </CardContent>
    </Card>
  );
}
